import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  Fade,
  Slide,
  Button,
  CircularProgress,
  Stack,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  styled,
} from "@mui/material";
import { ArrowUpward, ArrowDownward, Sort } from "@mui/icons-material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import CopyAllOutlinedIcon from "@mui/icons-material/CopyAllOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import { useTheme, alpha } from "@mui/material/styles";
import MainFunction from "./workflows"; // Preview
import NewWorkFlow from "./newworkflow";
import EditWorkFlow from "./editWorkFlow"; // Edit mode
import DuplicateWorkflow from "./duplicateWorkflow"; // Duplicate mode

// Styled components
const StyledModal = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    padding: theme.spacing(3),
    minWidth: 400,
    maxWidth: "90%",
    borderRadius: theme.spacing(2),
  },
}));

const EnhancedTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
  cursor: "pointer",
}));

import useGetMetadata from "@/hooks/useGetMetaData";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { PtwSettingsData } from "./workFlow-context";
import { unstable_batchedUpdates } from "react-dom"; // For batching state updates
import { useTranslation } from "react-i18next";
import PageHeader from "@/components/PageHeader";

const WorkFlowListView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutate: deleteWorkflow } = useDeleteData(
    "/api/v1/org-admin/workflows",
    ["workflows"]
  );
  const { WorkFlows, refetchWorkflows } = useGetMetadata();
  const { handleApiError, setToast } = useApiErrorHandler();

  const {
    setSelectedWorkflow,
    workflowAction,
    setWorkflowAction,
    selectedWorkflow,
  } = PtwSettingsData();

  // Check localStorage on mount - if there's a workflow being edited, restore edit mode
  useEffect(() => {
    try {
      const storedWorkflow = localStorage.getItem("workflowdetails");
      if (storedWorkflow) {
        const parsedWorkflow = JSON.parse(storedWorkflow);
        // If there's a workflow in localStorage, restore edit mode
        setSelectedWorkflow(parsedWorkflow);
        setWorkflowAction("edit");
      } else {
        // Otherwise, show list view
        setWorkflowAction("list");
      }
    } catch (error) {
      // If there's any error parsing localStorage, default to list view
      console.error("Error reading workflow from localStorage:", error);
      setWorkflowAction("list");
    }
  }, [setWorkflowAction, setSelectedWorkflow]); // Run once when component mounts

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handlePreviewClick = useCallback(
    (workflow) => {
      unstable_batchedUpdates(() => {
        setIsLoadingAction(true);
        setSelectedWorkflow(workflow);
      });
      // Use setTimeout to ensure state is updated before changing action
      setTimeout(() => {
        setWorkflowAction("preview");
        setIsLoadingAction(false);
      }, 0);
    },
    [setSelectedWorkflow, setWorkflowAction]
  );

  const handleEditClick = useCallback(
    (workflow) => {
      unstable_batchedUpdates(() => {
        setIsLoadingAction(true);

        setSelectedWorkflow(workflow);
      });
      // Use setTimeout to ensure state is updated before changing action
      setTimeout(() => {
        setWorkflowAction("edit");
        setIsLoadingAction(false);
      }, 0);
    },
    [setSelectedWorkflow, setWorkflowAction]
  );

  const handleDuplicateClick = useCallback(
    (workflow) => {
      unstable_batchedUpdates(() => {
        setIsLoadingAction(true);
        setSelectedWorkflow(workflow);
      });
      // Use setTimeout to ensure state is updated before changing action
      setTimeout(() => {
        setWorkflowAction("duplicate");
        setIsLoadingAction(false);
      }, 0);
    },
    [setSelectedWorkflow, setWorkflowAction]
  );

  const handleDeleteClick = useCallback((workflow) => {
    setWorkflowToDelete(workflow);
    setDeleteConfirmationOpen(true);
  }, []);

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteWorkflow(workflowToDelete.id);
      setToast({
        open: true,
        message: t("workFlowListView.deleteSuccessToast"),
        severity: "success",
      });
      refetchWorkflows();
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsDeleting(false);
      setWorkflowToDelete(null);
      setDeleteConfirmationOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
    setWorkflowToDelete(null);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key && prev.direction === "asc") {
        return { key, direction: "desc" };
      }
      return { key, direction: "asc" };
    });
  };

  const sortWorkflows = (workflows) => {
    if (!sortConfig.key || !workflows) return workflows;

    const sorted = [...workflows];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (key) {
        case "name":
          aValue = a.name || t("workFlowListView.unnamedWorkflow");
          bValue = b.name || t("workFlowListView.unnamedWorkflow");
          break;
        case "created_at":
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case "target":
          aValue = a.target || "";
          bValue = b.target || "";
          break;
        case "updated_at":
          aValue = new Date(a.updated_at).getTime();
          bValue = new Date(b.updated_at).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  // Conditional Render based on context with smooth transitions
  if (isLoadingAction) {
    return (
      <Fade in timeout={300}>
        <Typography>{t("workFlowListView.loadingText")}</Typography>
      </Fade>
    );
  }

  if (workflowAction === "preview") {
    return (
      <Slide direction="left" in timeout={600} mountOnEnter unmountOnExit>
        <Box>
          <MainFunction />
        </Box>
      </Slide>
    );
  }

  if (workflowAction === "edit") {
    return (
      <Slide direction="up" in timeout={500} mountOnEnter unmountOnExit>
        <Box>
          <EditWorkFlow />
        </Box>
      </Slide>
    );
  }

  if (workflowAction === "duplicate") {
    return (
      <Slide direction="up" in timeout={500} mountOnEnter unmountOnExit>
        <Box>
          <DuplicateWorkflow />
        </Box>
      </Slide>
    );
  }

  if (workflowAction === "newwf") {
    return (
      <Fade in timeout={500} mountOnEnter unmountOnExit>
        <Box>
          <NewWorkFlow />
        </Box>
      </Fade>
    );
  }

  const sortedWorkflows = sortWorkflows(WorkFlows);

  // Calculate workflow stats
  const totalWorkflows = sortedWorkflows?.length || 0;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Compact Enterprise Header */}
      <PageHeader
        icon={<AccountTreeIcon />}
        title={t("workFlowListView.title")}
        subtitle={t("workFlowListView.subtitle")}
        stats={[
          {
            label: `${totalWorkflows}`,
          },
        ]}
        actions={[
          {
            label: t("workFlowListView.addWorkflowButton"),
            icon: <AddIcon />,
            onClick: () => setWorkflowAction("newwf"),
            ariaLabel: t("workFlowListView.addWorkflowButtonAria"),
          },
        ]}
        t={t}
        translationPrefix="workFlowListView"
        compact={true}
      />

      {isLoadingAction && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            aria-label={t("workFlowListView.loadingProgressAria")}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      )}

      <Paper
        elevation={3}
        sx={{
          overflow: "hidden",
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          height: "calc(100vh - 280px)",
          display: "flex",
          flexDirection: "column",
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            overflow: "auto",
            transition: theme.transitions.create("all", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Table
            stickyHeader
            size="medium"
            aria-label={t("workFlowListView.tableAria")}
            sx={{
              width: "100%",
              tableLayout: "fixed",
              "& .MuiTableCell-root": {
                transition: theme.transitions.create(["width", "min-width"], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
              "& .MuiTableCell-head": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.background.paper, 0.8)
                    : theme.palette.grey[50],
              },
              "& .MuiTableCell-body": {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                <TableCell sx={{ width: "40%" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <strong>{t("workFlowListView.nameLabel")}</strong>
                    <IconButton
                      size="small"
                      onClick={() => handleSort("name")}
                      sx={{
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                      aria-label={t("workFlowListView.sortNameAria", {
                        direction:
                          sortConfig.key === "name" &&
                          sortConfig.direction === "asc"
                            ? t("workFlowListView.descendingAria")
                            : t("workFlowListView.ascendingAria"),
                      })}
                    >
                      {sortConfig.key === "name" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpward fontSize="small" />
                        ) : (
                          <ArrowDownward fontSize="small" />
                        )
                      ) : (
                        <Sort fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <strong>{t("workFlowListView.createdAtLabel")}</strong>
                    <IconButton
                      size="small"
                      onClick={() => handleSort("created_at")}
                      sx={{
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                      aria-label={t("workFlowListView.sortCreatedAtAria", {
                        direction:
                          sortConfig.key === "created_at" &&
                          sortConfig.direction === "asc"
                            ? t("workFlowListView.descendingAria")
                            : t("workFlowListView.ascendingAria"),
                      })}
                    >
                      {sortConfig.key === "created_at" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpward fontSize="small" />
                        ) : (
                          <ArrowDownward fontSize="small" />
                        )
                      ) : (
                        <Sort fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <strong>{t("workFlowListView.targetLabel")}</strong>
                    <IconButton
                      size="small"
                      onClick={() => handleSort("target")}
                      sx={{
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                      aria-label={t("workFlowListView.sortTargetAria", {
                        direction:
                          sortConfig.key === "target" &&
                          sortConfig.direction === "asc"
                            ? t("workFlowListView.descendingAria")
                            : t("workFlowListView.ascendingAria"),
                      })}
                    >
                      {sortConfig.key === "target" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpward fontSize="small" />
                        ) : (
                          <ArrowDownward fontSize="small" />
                        )
                      ) : (
                        <Sort fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ width: "150px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <strong>{t("workFlowListView.actionsLabel")}</strong>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedWorkflows && sortedWorkflows.length > 0 ? (
                sortedWorkflows.map((workflow) => (
                  <Fade in key={workflow.id} timeout={300}>
                    <EnhancedTableRow
                      onClick={() => handlePreviewClick(workflow)}
                      selected={selectedWorkflow?.id === workflow.id}
                      sx={{
                        backgroundColor:
                          selectedWorkflow?.id === workflow.id
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                        "&:hover": {
                          backgroundColor:
                            selectedWorkflow?.id === workflow.id
                              ? alpha(theme.palette.primary.main, 0.12)
                              : alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            minWidth: 0,
                          }}
                        >
                          <AccountTreeIcon
                            sx={{
                              color: theme.palette.primary.main,
                              opacity: 0.7,
                              fontSize: 20,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {workflow.name ||
                              t("workFlowListView.unnamedWorkflow")}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: theme.palette.text.secondary,
                          width: 160,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {new Date(workflow.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ minWidth: 140, maxWidth: 200 }}>
                        {workflow.target ? (
                          <Typography
                            variant="body2"
                            sx={{
                              display: "inline-block",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.info.main, 0.1),
                              color: theme.palette.info.main,
                              fontWeight: 500,
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {workflow.target}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 150,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <Tooltip title={t("workFlowListView.editTooltip")}>
                            <IconButton
                              onClick={() => handleEditClick(workflow)}
                              color="primary"
                              size="small"
                              sx={{
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                },
                              }}
                              aria-label={t("workFlowListView.editAria", {
                                workflowName:
                                  workflow.name ||
                                  t("workFlowListView.unnamedWorkflow"),
                              })}
                            >
                              <EditNoteOutlinedIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip
                            title={t("workFlowListView.duplicateTooltip")}
                          >
                            <IconButton
                              onClick={() => handleDuplicateClick(workflow)}
                              color="primary"
                              size="small"
                              sx={{
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1
                                  ),
                                },
                              }}
                              aria-label={t("workFlowListView.duplicateAria", {
                                workflowName:
                                  workflow.name ||
                                  t("workFlowListView.unnamedWorkflow"),
                              })}
                            >
                              <CopyAllOutlinedIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t("workFlowListView.deleteTooltip")}>
                            <IconButton
                              onClick={() => handleDeleteClick(workflow)}
                              color="error"
                              size="small"
                              sx={{
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                              aria-label={t("workFlowListView.deleteAria", {
                                workflowName:
                                  workflow.name ||
                                  t("workFlowListView.unnamedWorkflow"),
                              })}
                            >
                              <DeleteForeverOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </EnhancedTableRow>
                  </Fade>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 8,
                        px: 3,
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: 64,
                          color: theme.palette.text.disabled,
                          mb: 2,
                        }}
                      >
                        🔄
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {t("workFlowListView.noWorkflowsTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        {t("workFlowListView.noWorkflowsAvailable")}
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setWorkflowAction("newwf")}
                        sx={{ mt: 3 }}
                      >
                        {t("workFlowListView.addWorkflowButton")}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Modal */}
      <StyledModal
        open={deleteConfirmationOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-workflow-dialog-title"
      >
        <DialogTitle
          id="delete-workflow-dialog-title"
          sx={{
            fontWeight: 600,
            color: theme.palette.error.main,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <DeleteForeverOutlinedIcon />
          {t("workFlowListView.deleteDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "1rem", lineHeight: 1.6 }}>
            {t("workFlowListView.deleteDialogText")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={isDeleting}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("workFlowListView.cancelButton")}
          </Button>

          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={
              isDeleting ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteForeverOutlinedIcon />
              )
            }
            aria-label={t("workFlowListView.deleteButtonAria")}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: theme.shadows[2],
              "&:hover": {
                boxShadow: theme.shadows[4],
              },
            }}
          >
            {isDeleting
              ? t("workFlowListView.deletingText")
              : t("workFlowListView.deleteButton")}
          </Button>
        </DialogActions>
      </StyledModal>
    </Box>
  );
};

export default WorkFlowListView;
